<template>
  <div class="w-full relative" :class="disabledClasses">
    <l-dropdown
      v-model:open="selectorOpen"
      v-model:partially-selected-values="partiallySelectedValues"
      v-model="selectedArray"
      :allow-remove="allowRemove"
      :multiselect="multiselect"
      :options="options"
      :size="size"
      :search="search"
      :use-fuzzy="useFuzzy"
      :append-to="appendTo"
      :block-hide-on-click="blockHideOnClick"
      :fluid-size="fluidSize"
      :limit-height="limitHeight"
      :min-width="minWidth"
      @update:model-value="selectOption"
    >
      <template #topExtraContent>
        <slot name="topExtraContent" />
      </template>
      <template #content>
        <slot name="content" :options="options" />
      </template>
      <template #option="params">
        <slot name="option" v-bind="params" />
      </template>
      <template #extraContent>
        <slot name="extraContent" v-bind="{ closeSelector }" />
      </template>
      <div
        ref="selector"
        id="selector"
        tabindex="0"
        class="w-full leading-tight outline-none border border-1 border-n-100 dark:border-transparent flex justify-between items-center cursor-pointer"
        :class="[
          ...errorClasses,
          ...focusClasses,
          ...backgroundColors,
          ...sizeClasses,
          paddingClasses,
          'rounded',
          size === 'x-small' ? 'pr-2 !bg-n-50' : 'pr-3'
        ]"
        @click="openOrCloseSelector"
      >
        <slot name="value" :selected="optionSelected">
          <div class="flex-1 min-w-0 flex items-center">
            <l-icon v-if="leftIcon" :name="leftIcon" class="mr-4 text-n-200 dark:text-n-0" />
            <div class="flex items-center w-full">
              <div
                v-if="isColor"
                :style="{ background: text }"
                class="mx-6 my-2 w-full cursor-pointer rounded"
              />
              <div v-else-if="text" class="truncate" :class="textColors">
                {{ text }}
              </div>
              <p v-else class="text-n-200 dark:text-n-300">
                {{ props.placeholder }}
              </p>
            </div>
          </div>
        </slot>
        <div class="flex items-center">
          <l-icon v-if="rightIcon" :name="rightIcon" class="mr-4 text-n-200 dark:text-n-0" />
          <l-icon
            v-if="!canRemoveSelection"
            name="bracket-down"
            :size="size"
            class="transition-all duration-500 text-n-800 dark:text-n-100"
            :class="[selectorOpen ? 'rotate-180' : '', size === 'x-small' && 'ml-2']"
          />
          <l-icon
            v-else
            name="close"
            class="transition-all duration-500"
            :class="[...textColors]"
            @click.stop="clearSelection"
          />
        </div>
      </div>
    </l-dropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import type { TippyOptions } from 'vue-tippy'

import {
  useBackgroundColorsClasses,
  useDisabledClasses,
  useErrorClasses,
  useFocusClasses,
  usePaddingClasses,
  useSizeClasses,
  useTextColorsClasses
} from '../../components/classes'
import type { Option } from '../../components/dropdown/types'
import type { Size } from '../../components/types'
import LDropdown from '../dropdown/LDropdown.vue'
import type { LIconName } from '../icon.type'
import LIcon from '../LIcon.vue'

const { t } = useI18n()

type Props = {
  defaultValue?: string | string[] | null
  options: Option[]
  placeholder?: string
  /** Tells the component to always show the placeholder text instead of the selected value */
  isPlaceholderFixed?: boolean
  icon?: LIconName
  iconPosition?: string
  size?: Size
  allowRemove?: boolean
  wrongValue?: boolean
  multiselect?: boolean
  disabled?: boolean
  search?: boolean
  /**
   * A boolean flag that determines whether fuzzy matching is enabled or not.
   * When set to true, the logic will utilize fuzzy matching algorithms,
   * often used to handle approximate matches or tolerate slight differences in input values.
   * When set to false, the logic will enforce strict or exact matching.
   */
  useFuzzy?: boolean
  /** Blocks the select from being closed on click if true */
  blockHideOnClick?: boolean
  /** Change default `appendTo` of Tippy */
  appendTo?: TippyOptions['appendTo']
  /** If setted to true, max width will not be setted */
  fluidSize?: boolean
  /**
   * Specifies a limit for the maximum height of the dropdown content.
   * A value of `0` means there is no height constraint.
   * Can be used to restrict the vertical size of the content for better usability.
   */
  limitHeight?: number | 'auto'
  /**
   * Specifies a limit for the minimum width of the dropdown content.
   * A value of `0` means there is no width constraint.
   * Can be used to restrict the horizontal size of the content for better usability.
   */
  minWidth?: number
}

const props = withDefaults(defineProps<Props>(), {
  defaultValue: null,
  options: () => [],
  placeholder: '',
  isPlaceholderFixed: false,
  iconPosition: 'right',
  size: 'small',
  icon: undefined,
  allowRemove: false,
  wrongValue: false,
  multiselect: false,
  disabled: false,
  search: false,
  useFuzzy: true,
  blockHideOnClick: false,
  appendTo: undefined,
  fluidSize: false,
  limitHeight: 0,
  minWidth: 0
})

const model = defineModel<number | string | (string | number)[] | null>({
  default: null
})

const partiallySelectedValues = defineModel<(string | number)[]>('partiallySelectedValues', {
  default: []
})

const emit = defineEmits<{
  change: [string | number | (string | number)[] | null]
  blur: []
}>()

const selectorRef = useTemplateRef<HTMLElement>('selector')
const selectorOpen = defineModel<boolean>('selectorOpen', { default: false })

function closeSelector() {
  selectorRef?.value?.blur()
  selectorOpen.value = false
}

function openSelector() {
  selectorRef?.value?.focus()
  selectorOpen.value = true
}

function openOrCloseSelector() {
  return selectorOpen.value ? closeSelector() : openSelector()
}

function clearSelection() {
  model.value = null
  emit('change', null)
  emit('blur')
}

function selectOption(newSelection: (string | number)[]) {
  const value = newSelection ? (props.multiselect ? newSelection : newSelection[0]) : null
  model.value = value
  emit('change', value)
  emit('blur')
  if (!props.multiselect) closeSelector()
}

const selectedArray = computed(() => {
  if (model.value !== 0 && !model.value) return []
  return Array.isArray(model.value) ? model.value : [model.value]
})

const canRemoveSelection = computed(() => {
  return props.allowRemove && selectedArray.value?.length > 0
})

const labels = computed<Record<string, string>>(() => {
  const result: Record<string, string> = {}
  const queue = [...props.options]
  while (queue.length > 0) {
    const option = queue.shift()
    if (!option) continue
    if (option.children) {
      queue.push(...option.children)
    }
    result[option.value] = option.label
  }
  return result
})

const optionSelected = computed(() => {
  if (!model.value && model.value != 0) return null
  if (!Array.isArray(model.value)) {
    return props.options.find(option => option.value === model.value)!
  }

  const selectedOptions: Option[] = []
  for (const option of props.options) {
    if (model.value.includes(option.value)) {
      selectedOptions.push(option)
    } else if (option.children) {
      const allChildrenSelected = option.children.every(child =>
        (model.value as (string | number)[]).includes(child.value)
      )
      if (allChildrenSelected) {
        selectedOptions.push(option)
      } else {
        for (const child of option.children) {
          if (model.value.includes(child.value)) {
            selectedOptions.push(child)
          }
        }
      }
    }
  }
  return selectedOptions
})

const text = computed(() => {
  if (props.isPlaceholderFixed) return props.placeholder
  if (!model.value && model.value != 0) return

  if (!Array.isArray(model.value)) return labels.value[model.value]

  const items: string[] = []
  for (const option of props.options) {
    if (model.value.includes(option.value)) {
      items.push(labels.value[option.value])
    } else if (option.children && option.children.length) {
      const allChildrenSelected = option.children.every(child =>
        (model.value as (string | number)[]).includes(child.value)
      )
      if (allChildrenSelected) {
        items.push(option.label + `(${t('core.all')})`)
      } else {
        const childrenItems: string[] = []
        for (const child of option.children) {
          if (model.value.includes(child.value)) {
            childrenItems.push(child.label)
          }
        }
        if (childrenItems.length) items.push(option.label + `(${childrenItems.join(', ')})`)
      }
    }
  }
  return items.join(', ')
})

const isColor = computed(() => {
  if (!text.value) return false
  return text.value.match(`#([0-9]|[A-F]){6}`)
})

const leftIcon = computed(() => {
  return props.iconPosition === 'left' ? props.icon : undefined
})

const rightIcon = computed(() => {
  return props.iconPosition === 'right' ? props.icon : undefined
})

const disabledClasses = computed(() => useDisabledClasses(props.disabled))
const errorClasses = computed(() => useErrorClasses(props.wrongValue))
const sizeClasses = computed(() => useSizeClasses(props.size))
const paddingClasses = computed(() => usePaddingClasses(props.size))
const focusClasses = computed(() => useFocusClasses())
const backgroundColors = computed(() => useBackgroundColorsClasses())
const textColors = computed(() => useTextColorsClasses())
</script>
